import { faGear } from '@fortawesome/pro-solid-svg-icons/faGear';
import { faRightFromBracket } from '@fortawesome/pro-solid-svg-icons/faRightFromBracket';
import { useClientAccountOverview } from '@ukpostbox/client-data';
import {
	AvatarSize,
	ColourSchemeNames,
	DropdownMenu,
	DropdownMenuEntry,
	MainMenuEntryAddon,
	MainMenuEntryImage,
	MainMenuEntryLabel,
	MainMenuEntryWrapper,
	ToggleSwitch,
	ToggleType,
	Tooltip,
	ToolTipPlacement,
	useFramework,
	useLayout,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { ThemeSwitch } from '@ukpostbox/frontend-framework/lib/Components/ThemeSwitch/ThemeSwitch';
import { FormatCurrency } from '@ukpostbox/helpers';
import React, { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudMoon } from '@fortawesome/pro-solid-svg-icons/faCloudMoon';
import { faCompress } from '@fortawesome/pro-solid-svg-icons/faCompress';
import { faCreditCard } from '@fortawesome/pro-solid-svg-icons/faCreditCard';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons/faEllipsisVertical';
import { useNavigate } from 'react-router';
import { AuthContext } from 'react-oauth2-code-pkce';
import { useLayoutStore } from '@ukpostbox/frontend-framework/lib/Providers/Stores/LayoutStore';
import { useFrameworkStore } from '@ukpostbox/frontend-framework/lib/Providers/Stores/FrameworkStore';
import { faCog } from '@fortawesome/pro-solid-svg-icons/faCog';
import { UserAvatar } from '../../User/Components/UserAvatar';

type UserMenuType = {
	inHeader?: boolean;
	avatarSize?: AvatarSize;
	expanded?: boolean;
};

type UserMenuDropdownType = {
	activatorClass?: string;
	avatarSize?: AvatarSize;
	expanded?: boolean;
	customTrigger?: any;
};

type UserMetaType = {
	inline?: boolean;
};

function UserMeta(props: UserMetaType) {
	const { data } = useClientAccountOverview();

	return (
		<MainMenuEntryLabel title={`${data?.name ? data?.name : 'Username'}`} />
		/*
		<div className={`user-mini-overview-meta ${props.inline ? 'user-mini-overview-meta-inline' : ''}`}>
			<div className='user-mini-name'>{data?.name}</div>
			<span className='user-mini-balance'>{FormatCurrency(data?.balance || 0)}</span>
		</div> */
	);
}

function UserMenuDropdownMenu(props: UserMenuDropdownType) {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { data } = useClientAccountOverview();
	const { UpdateSizeMode, UpdateTheme } = useFramework();
	const { isUltraWide } = useLayout();

	const sidebarOpen = useLayoutStore((state) => state.sidebarOpen);

	const theme = useFrameworkStore((state) => state.theme);
	const sizeMode = useFrameworkStore((state) => state.sizeMode);

	const { logOut } = useContext(AuthContext);

	const CycleTheme = () => {
		if (theme === ColourSchemeNames.AUTO) {
			UpdateTheme(ColourSchemeNames.LIGHT);
		}
		if (theme === ColourSchemeNames.LIGHT) {
			UpdateTheme(ColourSchemeNames.DARK);
		}
		if (theme === ColourSchemeNames.DARK) {
			UpdateTheme(ColourSchemeNames.AUTO);
		}
	};

	return (
		<DropdownMenu
			title={t('actions.manageAccount')}
			trigger={
				props.customTrigger ? (
					props.customTrigger
				) : (
					<MainMenuEntryWrapper
						icon={faCog}
						title={t('actions.manageAccount')}
						extraClass='side-navigation-user-entry'
					>
						<MainMenuEntryImage
							image={<UserAvatar size={props.avatarSize ? props.avatarSize : AvatarSize.Medium} />}
						/>
						{(props.expanded || sidebarOpen || isUltraWide) && (
							<>
								<UserMeta inline={props.expanded} />
								<MainMenuEntryAddon addon={<FontAwesomeIcon icon={faEllipsisVertical} />} />
							</>
						)}
					</MainMenuEntryWrapper>
				)
			}
		>
			{data && (
				<DropdownMenuEntry
					title={t('actions.topUp')}
					onClick={() => navigate('/manage-account/payment/top-up')}
					icon={faCreditCard}
					addonContentEnd={<small className='fade'>{FormatCurrency(data?.balance)}</small>}
				/>
			)}

			<DropdownMenuEntry
				title={t('actions.settings')}
				onClick={() => navigate('/manage-account')}
				icon={faGear}
			/>

			<DropdownMenuEntry title={t('labels.divider')} sectionDivider />

			<DropdownMenuEntry title={t('actions.logOut')} onClick={() => logOut()} icon={faRightFromBracket} />

			<DropdownMenuEntry title={t('labels.divider')} sectionDivider />

			<DropdownMenuEntry title={t('labels.preferences')} sectionHeader />

			<DropdownMenuEntry
				title={t('framework.labels.colourScheme')}
				onClick={(e) => {
					e.stopPropagation();
					CycleTheme();
				}}
				icon={faCloudMoon}
				addonContentEnd={<ThemeSwitch />}
			/>

			<DropdownMenuEntry
				title={t('actions.compactMode')}
				onClick={() => UpdateSizeMode(sizeMode !== 'small' ? 'small' : '')}
				icon={faCompress}
				addonContentEnd={
					<ToggleSwitch
						type={ToggleType.Switch}
						label={t('actions.compactMode')}
						name='compactMode'
						defaultChecked={sizeMode === 'small'}
						hideLabel
					/>
				}
			/>

			<DropdownMenuEntry
				title={t('actions.largeMode')}
				onClick={() => UpdateSizeMode(sizeMode !== 'large' ? 'large' : '')}
				icon={faCompress}
				addonContentEnd={
					<ToggleSwitch
						type={ToggleType.Switch}
						label={t('actions.largeMode')}
						name='largeMode'
						defaultChecked={sizeMode === 'large'}
						hideLabel
					/>
				}
			/>
		</DropdownMenu>
	);
}

function UserMenu(props: UserMenuType) {
	const { t } = useTranslation();
	const { isTiny, isSmall, isUltraWide } = useLayout();

	const sidebarOpen = useLayoutStore((state) => state.sidebarOpen);

	return (
		<Tooltip
			content={t('actions.manageAccount')}
			position={ToolTipPlacement.Right}
			disabled={sidebarOpen || isTiny || isSmall || isUltraWide}
		>
			<UserMenuDropdownMenu
				activatorClass='user-overview-nav'
				avatarSize={props.avatarSize}
				expanded={props.expanded}
			/>
		</Tooltip>
	);
}

export default UserMenu;

export { UserMenuDropdownMenu };
